import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEvent, useFinancials } from '@/lib/api/queries'
import { DEFAULT_EVENT_TIMEZONE, eventLocalToServerNaive } from '@/lib/eventTime'
import { DateFilter } from '@/components/ui/date-filter'
import { useState, useMemo, type ReactNode } from 'react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { usePermission, useAllPermissions } from '@/lib/permissions'
import { AccessDenied } from '@/components/permission-guard'
import { useAuth } from '@/lib/auth'
import { apiClient } from '@/lib/api/client'
import { ExportDropdown, XLSX_FORMAT, PDF_FORMAT } from '@/components/export-dropdown'
import { PlusCircle, MinusCircle, Database, TrendingUp, DollarSign, Disc, MoreHorizontal, CreditCard, Smartphone } from 'lucide-react'
import { cn } from '@/lib/utils'
import { format, parseISO } from 'date-fns'
import type { TenderAmounts } from '@/lib/api/types'

export const Route = createFileRoute('/_authenticated/events/$eventId/financials')({
  component: FinancialsPage,
})

type TableTab = 'date' | 'system' | 'users'

function FinancialsPage() {
  const { eventId } = Route.useParams()
  const eventIdNum = Number(eventId)
  const navigate = useNavigate()
  // Declared before dateParams: the memo reads event?.timezone during render.
  const { data: event } = useEvent(eventIdNum)
  const { token } = useAuth()

  // Permission check
  const canViewFinancials = usePermission('can_view_financials')
  const canExportFinancials = useAllPermissions(['can_export_reports', 'can_view_financials'])
  
  const [activeTab, setActiveTab] = useState<TableTab>('date')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  // Calculate date range params for API
  const dateParams = useMemo(() => {
    if (!startDate && !endDate) return undefined
    const params: { created_at__gte?: string; created_at__lte?: string } = {}
    // Typed values are venue wall-clock; the endpoint parses naive strings
    // in the SERVER timezone, so re-anchor venue -> server wall-clock here
    // (wire format unchanged — see src/lib/eventTime.ts).
    const tz = event?.timezone || DEFAULT_EVENT_TIMEZONE
    if (startDate) {
      params.created_at__gte = eventLocalToServerNaive(startDate, tz)
    }
    if (endDate) {
      params.created_at__lte = eventLocalToServerNaive(endDate, tz)
    }
    return params
  }, [startDate, endDate, event?.timezone])
  
  const { data: financials, isLoading, error } = useFinancials(eventIdNum, dateParams)
  
  // Permission guard
  if (!canViewFinancials) {
    return <AccessDenied message="You don't have permission to view financials." />
  }

  // Extract transaction data by action type (like the old project does)
  const transactionsByAction = useMemo(() => {
    const transactions = financials?.transactions || []
    const result: Record<string, { sum: number; currency_sum: number; count: number }> = {}
    
    for (const txn of transactions) {
      result[txn.action] = {
        sum: txn.sum || 0,
        currency_sum: txn.currency_sum || 0,
        count: txn.count || 0,
      }
    }
    return result
  }, [financials?.transactions])

  // Get transaction data by action type
  const addCreditData = transactionsByAction['addCredit'] || { sum: 0, currency_sum: 0, count: 0 }
  const voucherCreditData = transactionsByAction['voucherCredit'] || { sum: 0, currency_sum: 0, count: 0 }
  // creditRefund data is used via refund_breakdown.credit_refunds which has more detail

  // Extract credit refunds from refund_breakdown
  const creditRefunds = financials?.refund_breakdown?.credit_refunds
  const creditRefundsSum = creditRefunds?.sum || creditRefunds?.tokens || (creditRefunds?.tokens_abs ? -creditRefunds.tokens_abs : 0) || 0
  const creditRefundsAmount = creditRefunds?.amount || (creditRefunds?.amount_abs ? -creditRefunds.amount_abs : 0) || 0

  // Get token value from event
  const tokenValue = event?.token_value || 2

  // Token stats
  const tokenSummary = financials?.token_summary || {}
  const totalTokensCreated = financials?.total_tokens_created || 0

  // Net tokens in circulation = gross issued + refunds (refunds are negative).
  // Prefer the API-provided value; fall back to computing from gross + refunds.
  const netTokensInCirculation = tokenSummary.net_tokens_in_circulation ?? (totalTokensCreated + creditRefundsSum)

  // Open balance = tokens still in user wallets (not yet redeemed at vendors)
  const openBalance = financials?.event_open_token_balance || 0

  // Tokens spent at vendors = net in circulation - tokens still in user wallets
  const tokensIssued = Math.round(netTokensInCirculation)
  const tokensSpent = Math.round(netTokensInCirculation - openBalance)
  const tokensRemaining = Math.round(openBalance)

  // Alias used by the Event Financial Report section
  const totalTokensSpentCalc = tokensSpent

  // Income, Expenses and Payout are the PDF/XLSX report's figures for the
  // same rows, token and Direct Pay sales alike: what the processor collected.
  // Cash desk rows and no-charge credits are in neither Total Collected nor
  // the payout, so Cash Reloads shows beside them, and the EventPay fee on cash
  // desk money, which the processor never withheld, shows as owing. The
  // report's sections render only with the report in hand: a figure that
  // failed to load must not read as $0.00. The processing fee is "pending" while any card transaction in the
  // range awaits settlement, so it (and the payout) can still move; see the
  // backend's PROCESSING_COSTS.md.
  const report = financials?.report
  const tenders = financials?.tenders

  // Chart data from by_date - sorted chronologically (oldest to newest)
  const chartData = useMemo(() => {
    const byDate = financials?.by_date || []
    return byDate
      .filter(entry => {
        // Skip dates where all values are 0
        const tokensPurchased = entry.tokens_purchased || 0
        const tokensSpent = entry.tokens_spent || 0
        return tokensPurchased !== 0 || tokensSpent !== 0
      })
      .map(entry => ({
        date: entry.timestamp__date || '',
        tokensPurchased: Math.round(entry.tokens_purchased || 0),
        tokensSpent: Math.round(Math.abs(entry.tokens_spent || 0)),
      }))
      .sort((a, b) => a.date.localeCompare(b.date)) // Sort by date ascending
  }, [financials?.by_date])

  // By Date table data
  const byDateData = useMemo(() => {
    const byDate = financials?.by_date || []
    return byDate
      .filter(entry => {
        // Skip rows where all values are 0
        const tokensPurchased = entry.tokens_purchased || 0
        const tokensSpent = entry.tokens_spent || 0
        return tokensPurchased !== 0 || tokensSpent !== 0
      })
      .map(entry => ({
        date: entry.timestamp__date || '',
        tokensAdded: Math.round(entry.tokens_purchased || 0),
        tokensSpentSent: Math.round(Math.abs(entry.tokens_spent || 0)),
        netFlow: Math.round((entry.tokens_purchased || 0) + (entry.tokens_spent || 0)),
      }))
  }, [financials?.by_date])

  // By System table data (shows user data with tokens reloaded/refunded)
  const bySystemData = useMemo(() => {
    const bySystem = financials?.by_system || []
    return bySystem
      .filter(entry => {
        // Skip rows where both tokens purchased and spent are 0
        const tokensPurchased = entry.tokens_purchased || 0
        const tokensSpent = entry.tokens_spent || 0
        return tokensPurchased !== 0 || tokensSpent !== 0
      })
      .map(entry => {
        const tokensPurchased = entry.tokens_purchased || 0
        const tokensSpentRaw = entry.tokens_spent || 0
        const tokensSpentAbs = Math.abs(tokensSpentRaw)
        const netFlow = tokensPurchased + tokensSpentRaw
        
        return {
          userId: entry.from_user__id,
          email: entry.from_user__email || '',
          numReloads: entry.num_reloads || 0,
          tokensReloaded: Math.round(tokensPurchased),
          tokensRefunded: Math.round(tokensSpentAbs),
          netFlow: Math.round(netFlow),
        }
      })
  }, [financials?.by_system])

  // By Users table data
  const byUsersData = useMemo(() => {
    const byUsers = financials?.by_users || []
    return byUsers
      .filter(entry => {
        // Skip rows where all values are 0
        const tokensPurchased = entry.tokens_purchased || 0
        const tokensSpent = entry.tokens_spent || 0
        return tokensPurchased !== 0 || tokensSpent !== 0
      })
      .slice(0, 50)
      .map(entry => ({
        userId: entry.user_id || 0,
        tokensPurchased: Math.round(entry.tokens_purchased || 0),
        tokensSpent: Math.round(Math.abs(entry.tokens_spent || 0)),
        netFlow: Math.round((entry.tokens_purchased || 0) + (entry.tokens_spent || 0)),
      }))
  }, [financials?.by_users])

  const formatNumber = (num: number) => num.toLocaleString()
  const formatTokenStat = (num: number) => (financials ? formatNumber(num) : isLoading ? '...' : '-')
  const formatCurrency = (num: number) => `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  return (
    <div className="max-w-[1200px] w-full mx-auto px-4 md:px-8">
      <div className="min-h-[calc(100vh-8rem)] flex flex-col py-8">
        {/* Header with Date Range */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 w-full">
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100">Financials</h1>
          <div className="flex items-center gap-2 mt-3 sm:mt-0">
            {canExportFinancials && (
              <ExportDropdown
                formats={[XLSX_FORMAT, PDF_FORMAT]}
                onExport={async (fmt) => {
                  if (!token) throw new Error('Not authenticated')
                  await apiClient.exportFinancials(token, eventIdNum, fmt as 'xlsx' | 'pdf')
                }}
              />
            )}
          <div className="relative">
            <DateFilter
              value={{ gte: startDate || undefined, lte: endDate || undefined }}
              onChange={(v) => { setStartDate(v.gte ?? ''); setEndDate(v.lte ?? '') }}
              eventTimeZone={event?.timezone}
              defaultMonth={event?.start_date_time ? new Date(event.start_date_time) : undefined}
              label="Date range"
            />
          </div>
          </div>
      </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10 w-full">
          {/* Tokens Issued */}
          <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-3 pt-4 md:p-4">
            <div className="flex items-center">
              <div className="p-2 rounded-md bg-green-50 dark:bg-green-900/20 mr-3">
                <PlusCircle className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
                  {formatTokenStat(tokensIssued)}
                </p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 font-medium">Tokens Issued</p>
              </div>
            </div>
          </div>
          
          {/* Tokens Spent */}
          <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-3 pt-4 md:p-4">
            <div className="flex items-center">
              <div className="p-2 rounded-md bg-red-50 dark:bg-red-900/20 mr-3">
                <MinusCircle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
                  {formatTokenStat(tokensSpent)}
                </p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 font-medium">Tokens Spent</p>
              </div>
            </div>
          </div>
          
          {/* Tokens Remaining */}
          <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-3 pt-4 md:p-4">
            <div className="flex items-center">
              <div className="p-2 rounded-md bg-purple-50 dark:bg-purple-900/20 mr-3">
                <Database className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
                  {formatTokenStat(tokensRemaining)}
                </p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 font-medium">Tokens Remaining</p>
              </div>
            </div>
          </div>
        </div>

        {/* Token Flow Over Time Chart */}
        <div className="w-full bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden mb-6">
          <div className="px-6 pt-6">
            <h2 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Token Flow Over Time</h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4 font-medium">Tokens purchased and spent by day</p>
          </div>
          <div className="px-4 pb-6">
            {isLoading ? (
              <div className="h-[450px] flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
              </div>
            ) : error ? (
              <div className="h-[450px] flex items-center justify-center text-red-500">
                Failed to load chart data
              </div>
            ) : chartData.length === 0 ? (
              <div className="h-[450px] flex items-center justify-center text-neutral-500">
                No data available for the selected date range
            </div>
            ) : (
              <ResponsiveContainer width="100%" height={450}>
                <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-neutral-200 dark:stroke-neutral-800" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    className="text-neutral-500 dark:text-neutral-400"
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    className="text-neutral-500 dark:text-neutral-400"
                  />
                <Tooltip
                  contentStyle={{
                      backgroundColor: 'var(--tooltip-bg, white)',
                      border: '1px solid var(--tooltip-border, #e5e5e5)',
                    borderRadius: '8px',
                      fontSize: '12px',
                    }}
                  />
                  <Legend 
                    verticalAlign="bottom"
                    height={36}
                    formatter={(value: string) => (
                      <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{value}</span>
                    )}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="tokensPurchased" 
                    name="Tokens Purchased"
                    stroke="#10b981" 
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="tokensSpent" 
                    name="Tokens Spent"
                    stroke="#ef4444" 
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Tab Navigation for Table Views */}
        <div className="mb-6 border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex space-x-6 overflow-x-auto">
            <button
              onClick={() => setActiveTab('date')}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'date'
                  ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-500'
                  : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
              )}
            >
              By Date
            </button>
            <button
              onClick={() => setActiveTab('system')}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'system'
                  ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-500'
                  : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
              )}
            >
              Support Reloads
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'users'
                  ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-500'
                  : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
              )}
            >
              By Users
            </button>
          </div>
        </div>

        {/* Table Content */}
        <div className="mb-10 w-full">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : activeTab === 'date' ? (
            <ByDateTable data={byDateData} />
          ) : activeTab === 'system' ? (
            <>
              <div className="flex justify-end px-1 pb-2">
                <Link
                  to="/events/$eventId/financials/support-reloads"
                  params={{ eventId }}
                  className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                >
                  View Support Reloads →
                </Link>
              </div>
              <BySystemTable
                data={bySystemData}
                onSelectAccount={(email) =>
                  navigate({
                    to: '/events/$eventId/financials/support-reloads',
                    params: { eventId },
                    search: { staff: email },
                  })
                }
              />
            </>
          ) : (
            <ByUsersTable data={byUsersData} />
          )}
        </div>

        {/* Event Financial Report */}
        <div className="mb-10 w-full">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">Event Financial Report</h2>
          </div>
          
          <div className="space-y-6">
            {report && tenders ? (
              <>
                {/* Token Overview Section */}
                <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-medium mb-4 text-neutral-900 dark:text-neutral-100 flex items-center">
                    <Disc className="w-5 h-5 mr-2 text-blue-500" />
                    Token Overview
                  </h3>
                  <div className="space-y-3">
                    <ReportRow 
                      label="Tokens Sold" 
                      value={`${formatNumber(Math.round(addCreditData.sum || 0))} tokens (${formatCurrency(addCreditData.currency_sum || 0)})`} 
                    />
                    <ReportRow 
                      label="Refunds" 
                      value={creditRefundsSum !== 0 ? `${formatNumber(Math.round(creditRefundsSum))} tokens (-${formatCurrency(Math.abs(creditRefundsAmount))})` : '0'}
                      valueClassName="text-red-600 dark:text-red-400"
                    />
                    <ReportRow 
                      label="Token Vouchers" 
                      value={`${formatNumber(Math.round(voucherCreditData.sum || 0))} tokens (${formatCurrency(voucherCreditData.currency_sum || 0)})`} 
                    />
                    <ReportRow 
                      label="Total Tokens Issued" 
                      value={`${formatNumber(Math.round(netTokensInCirculation))} (${formatCurrency(netTokensInCirculation * tokenValue)})`}
                      highlight
                    />
                    <ReportRow 
                      label="Total Tokens Spent" 
                      value={`${formatNumber(Math.round(totalTokensSpentCalc))} (${formatCurrency(totalTokensSpentCalc * tokenValue)})`} 
                    />
                    <ReportRow 
                      label="Total Tokens Unspent" 
                      value={`${formatNumber(Math.round(openBalance))} (${formatCurrency(openBalance * tokenValue)})`} 
                    />
                  </div>
                </div>

                {/* Income Section */}
                <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-medium mb-4 text-neutral-900 dark:text-neutral-100 flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2 text-emerald-500" />
                    Income
                  </h3>
                  <div className="space-y-3">
                    <ReportRow
                      label="Tokens Sold"
                      value={`${formatNumber(Math.round(report.tokens_sold))} tokens (${formatCurrency(report.total_card_amount)})`}
                    />
                    <ReportRow
                      label="Refunds"
                      value={report.card_refund_tokens ? `${formatNumber(Math.round(report.card_refund_tokens))} tokens (${formatMoney(report.card_refund_currency)})` : '0'}
                      valueClassName="text-red-600 dark:text-red-400"
                    />
                    <ReportRow
                      label="Total Sold"
                      value={`${formatNumber(Math.round(report.tokens_sold + report.card_refund_tokens))} tokens (${formatCurrency(report.total_sold)})`}
                      highlight
                    />
                    <ReportRow
                      label="Additional Fees"
                      value={formatCurrency(report.net_additional_fee)}
                    />
                    <ReportRow
                      label="Token Income"
                      value={formatCurrency(report.token_income)}
                      highlight
                    />
                    <ReportRow
                      label="Tax Collected"
                      value={formatCurrency(report.tax_collected)}
                    />
                    {report.has_direct_pay && (
                      <>
                        <ReportRow label="Direct Pay Sales" value={formatMoney(report.direct_pay_gross)} />
                        <ReportRow
                          label="Direct Pay Refunds"
                          value={formatMoney(report.direct_pay_refunds)}
                          valueClassName="text-red-600 dark:text-red-400"
                        />
                        <ReportRow label="Direct Pay Additional Fees" value={formatMoney(report.direct_pay_additional_fee)} />
                        <ReportRow label="Direct Pay Tax" value={formatMoney(report.direct_pay_tax)} />
                        <ReportRow label="Direct Pay Income" value={formatMoney(report.direct_pay_collected)} highlight />
                      </>
                    )}
                    <ReportRow
                      label="Total Collected"
                      value={formatCurrency(report.total_collected)}
                      highlight
                    />
                    <div className="border-t border-neutral-200 dark:border-neutral-800 pt-3">
                      <ReportRow
                        label="Cash Reloads (collected on site, not in payout)"
                        value={formatCurrency(tenders.channels.find((channel) => channel.key === 'cash_reload')?.net ?? 0)}
                      />
                    </div>
                  </div>
                </div>

                {/* Expenses Section */}
                <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-medium mb-4 text-neutral-900 dark:text-neutral-100 flex items-center">
                    <DollarSign className="w-5 h-5 mr-2 text-orange-500" />
                    Expenses
                  </h3>
                  <div className="space-y-3">
                    <ReportRow
                      label="EventPay Fee Paid"
                      value={formatCurrency(report.platform_fee_paid)}
                      highlight
                    />
                    <ReportRow
                      label="Processing Fee"
                      value={formatCurrency(report.processing_fee)}
                      badge={report.processing_fee_pending ? <PendingBadge count={report.processing.pending_transactions} /> : undefined}
                      highlight
                    />
              </div>
                </div>
            
                {/* Payout Section */}
                <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-medium mb-4 text-neutral-900 dark:text-neutral-100 flex items-center">
                    <DollarSign className="w-5 h-5 mr-2 text-emerald-500" />
                    EventPay Payout
                  </h3>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-3">
                    <code className="bg-neutral-100 dark:bg-neutral-900 px-2 py-1 rounded">
                      Total Collected - EventPay Fee Paid - Processing Fee
                    </code>
                  </p>
                  <div className="flex justify-between items-center p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-md">
                    <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Net Payout</span>
                    <span className="text-sm font-bold text-emerald-600 dark:text-emerald-500">
                      {formatMoney(report.total_payout)}
                    </span>
                  </div>
                  {Math.round(report.platform_fee_owing * 100) !== 0 && (
                    <div className="mt-3">
                      <ReportRow
                        label="EventPay Fee Owing (on cash desk money, not in this payout)"
                        value={formatMoney(report.platform_fee_owing)}
                      />
                    </div>
                  )}
                </div>

                {/* Payments Section */}
                <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-medium mb-1 text-neutral-900 dark:text-neutral-100 flex items-center">
                    <CreditCard className="w-5 h-5 mr-2 text-blue-500" />
                    Payments
                  </h3>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">Before tax and fees.</p>
                  <AmountsTable
                    headers={['Payment type', 'Count', 'Sales', 'Refunds', 'Net']}
                    rows={tenders.channels.map((channel) => ({
                      key: channel.key,
                      cells: [channel.label, ...amountCells(channel)],
                    }))}
                    emptyMessage="No payments in the selected date range."
                  />
                </div>

                {/* Card Present Section */}
                {tenders.card_present.by_model.length > 0 && (
                  <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-6 shadow-sm">
                    <h3 className="text-lg font-medium mb-1 text-neutral-900 dark:text-neutral-100 flex items-center">
                      <Smartphone className="w-5 h-5 mr-2 text-blue-500" />
                      Card Present
                    </h3>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
                      Before tax and fees, net of refunds.
                    </p>
                    <AmountsTable
                      headers={['Reader', 'Sales', 'Reloads', 'Net']}
                      rows={tenders.card_present.by_model.map((model) => ({
                        key: model.device_model,
                        cells: [model.label, formatMoney(model.sales.net), formatMoney(model.reloads.net), formatMoney(model.net)],
                      }))}
                      footer={[
                        'Total',
                        formatMoney(tenders.card_present.by_model.reduce((sum, model) => sum + model.sales.net, 0)),
                        formatMoney(tenders.card_present.by_model.reduce((sum, model) => sum + model.reloads.net, 0)),
                        formatMoney(tenders.card_present.net),
                      ]}
                    />
                    {tenders.card_present.by_location.length > 0 && (
                      <>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-6 mb-3">
                          Reader sales by location. A reload does not record where the reader was.
                        </p>
                        <AmountsTable
                          headers={['Vendor', 'Location', 'Reader', 'Count', 'Sales', 'Refunds', 'Net']}
                          rows={tenders.card_present.by_location.map((row) => ({
                            key: `${row.vendor_event_id}-${row.device_model}`,
                            cells: [row.vendor || '-', row.location || '-', row.label, ...amountCells(row)],
                          }))}
                        />
                      </>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-6 shadow-sm">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                  </div>
                ) : (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    The financial report could not be loaded. Refresh to try again.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// A negative amount as -$6.00, not $-6.00.
function formatMoney(amount: number) {
  const dollars = Math.abs(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return `${amount < 0 ? '-' : ''}$${dollars}`
}

function amountCells(row: TenderAmounts) {
  return [row.count.toLocaleString(), formatMoney(row.gross), formatMoney(row.refunds), formatMoney(row.net)]
}

// A table of amounts inside a report section
function AmountsTable({ headers, rows, footer, emptyMessage }: {
  headers: string[]
  rows: Array<{ key: string; cells: string[] }>
  footer?: string[]
  emptyMessage?: string
}) {
  return (
    <div className="w-full overflow-x-auto">
      <div className="inline-block min-w-full align-middle">
        <div className="overflow-hidden border border-neutral-200 dark:border-neutral-800 sm:rounded-lg">
          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
            <thead className="bg-neutral-50 dark:bg-neutral-900">
              <tr>
                {headers.map((header) => (
                  <th key={header} scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{header}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-[#131313] divide-y divide-neutral-200 dark:divide-neutral-800">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={headers.length} className="px-6 py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.key}>
                    {row.cells.map((cell, index) => (
                      <td key={index} className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-neutral-100">{cell}</td>
                    ))}
                  </tr>
                ))
              )}
              {footer && (
                <tr className="bg-neutral-50 dark:bg-neutral-900">
                  {footer.map((cell, index) => (
                    <td key={index} className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900 dark:text-neutral-100">{cell}</td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// Report Row Component
function ReportRow({ 
  label, 
  value, 
  highlight = false,
  valueClassName = '',
  badge,
}: { 
  label: string
  value: string
  highlight?: boolean
  valueClassName?: string
  /** Rendered beside the value, e.g. a "Pending" tag while a figure is still settling. */
  badge?: ReactNode
}) {
  return (
    <div className={cn(
      'flex justify-between items-center p-2 rounded-md transition-colors',
      highlight 
        ? 'bg-emerald-50 dark:bg-emerald-900/20' 
        : 'hover:bg-neutral-50 dark:hover:bg-neutral-900'
    )}>
      <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{label}</span>
      <span className="inline-flex items-center gap-2">
        {badge}
        <span className={cn(
          'text-sm font-medium text-neutral-900 dark:text-neutral-100',
          valueClassName
        )}>
          {value}
        </span>
      </span>
    </div>
  )
}

// Shown beside a figure that will still change once the processor settles
// the remaining card transactions.
function PendingBadge({ count }: { count?: number }) {
  const title = count
    ? `${count.toLocaleString()} card transaction${count === 1 ? '' : 's'} awaiting settlement`
    : 'Card transactions awaiting settlement'
  return (
    <span
      title={title}
      className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:text-amber-300"
    >
      Pending
    </span>
  )
}

// By Date Table Component
function ByDateTable({ data }: { data: Array<{ date: string; tokensAdded: number; tokensSpentSent: number; netFlow: number }> }) {
  return (
    <div className="w-full overflow-x-auto">
      <div className="inline-block min-w-full align-middle">
        <div className="overflow-hidden border border-neutral-200 dark:border-neutral-800 sm:rounded-lg">
          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
            <thead className="bg-neutral-50 dark:bg-neutral-900">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Date</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Tokens Added</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Tokens Spent/Sent</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Net Flow</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-[#131313] divide-y divide-neutral-200 dark:divide-neutral-800">
              {data.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">
                    No date data available for the selected date range.
                  </td>
                </tr>
              ) : (
                data.map((entry, idx) => (
                  <tr key={idx}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900 dark:text-neutral-100">
                      {entry.date ? format(parseISO(entry.date), 'MMM dd, yyyy') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 dark:text-blue-500">
                      {entry.tokensAdded.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-500">
                      {entry.tokensSpentSent.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {entry.netFlow >= 0 ? (
                        <span className="text-green-600 dark:text-green-500">+{entry.netFlow.toLocaleString()}</span>
                      ) : (
                        <span className="text-red-600 dark:text-red-500">{entry.netFlow.toLocaleString()}</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// By System Table Component
function BySystemTable({ data, onSelectAccount }: {
  data: Array<{ userId?: number; email: string; numReloads: number; tokensReloaded: number; tokensRefunded: number; netFlow: number }>
  onSelectAccount?: (email: string) => void
}) {
  return (
    <div className="w-full overflow-x-auto">
      <div className="inline-block min-w-full align-middle">
        <div className="overflow-hidden border border-neutral-200 dark:border-neutral-800 sm:rounded-lg">
          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
            <thead className="bg-neutral-50 dark:bg-neutral-900">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Support Account</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Reloads</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Tokens Reloaded</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Tokens Refunded</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Net Flow</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-[#131313] divide-y divide-neutral-200 dark:divide-neutral-800">
              {data.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">
                    No system data available for the selected date range.
                  </td>
                </tr>
              ) : (
                data.map((entry, idx) => (
                  <tr
                    key={entry.userId ?? idx}
                    onClick={onSelectAccount && entry.email ? () => onSelectAccount(entry.email) : undefined}
                    className={onSelectAccount && entry.email
                      ? 'cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/50'
                      : undefined}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-neutral-100">
                      {entry.email || 'System User'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600 dark:text-neutral-400">
                      {entry.numReloads.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 dark:text-blue-500">
                      {entry.tokensReloaded.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-500">
                      {entry.tokensRefunded.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {entry.netFlow >= 0 ? (
                        <span className="text-green-600 dark:text-green-500">+{entry.netFlow.toLocaleString()}</span>
                      ) : (
                        <span className="text-red-600 dark:text-red-500">{entry.netFlow.toLocaleString()}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button className="text-neutral-400 hover:text-neutral-500 dark:hover:text-neutral-300">
                        <MoreHorizontal className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// By Users Table Component
function ByUsersTable({ data }: { data: Array<{ userId: number; tokensPurchased: number; tokensSpent: number; netFlow: number }> }) {
  return (
    <div className="w-full overflow-x-auto">
      <div className="inline-block min-w-full align-middle">
        <div className="overflow-hidden border border-neutral-200 dark:border-neutral-800 sm:rounded-lg">
          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
            <thead className="bg-neutral-50 dark:bg-neutral-900">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">User ID</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Tokens Purchased</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Tokens Spent</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Net Balance</th>
                </tr>
              </thead>
            <tbody className="bg-white dark:bg-[#131313] divide-y divide-neutral-200 dark:divide-neutral-800">
              {data.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">
                    No user data available for the selected date range.
                  </td>
                </tr>
              ) : (
                data.map((entry, idx) => (
                  <tr key={idx}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600 dark:text-blue-500">
                      #{entry.userId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 dark:text-green-500">
                      +{entry.tokensPurchased.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-500">
                      -{entry.tokensSpent.toLocaleString()}
                    </td>
                    <td className={cn(
                      'px-6 py-4 whitespace-nowrap text-sm font-medium',
                      entry.netFlow >= 0 ? 'text-green-600 dark:text-green-500' : 'text-red-600 dark:text-red-500'
                    )}>
                      {entry.netFlow >= 0 ? '+' : ''}{entry.netFlow.toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
              </tbody>
            </table>
          </div>
      </div>
    </div>
  )
}
